import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Footer from './Footer'
import { ChevronLeftIcon, PrimaryButton } from './ui'
import { useApi } from '../services/api'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import { usePortalAuth } from '../auth/authContexts'
import {
  shouldSignOutForPolicyLinkOtpError,
  storePolicyLinkOtpLockoutMessage,
  type PolicyLinkOtpApiErrorBody,
} from '../utils/policyLinkOtpLockout'

const RESEND_COOLDOWN_SECONDS = 60
/** Fallback when navigation state has no expiry (matches policy-link OTP TTL). */
const POLICY_LINK_OTP_TTL_MS = 10 * 60 * 1000

export type PolicyLinkOtpLocationState = {
  challengeId: string
  maskedEmail: string
  expiresAtUtc?: string
  linkAdditionalPolicy?: boolean
}

export default function PolicyLinkOtpVerification() {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = usePortalAuth()
  const { verifyPolicyLinkOtp, resendPolicyLinkOtp } = useApi()
  const state = (location.state ?? null) as PolicyLinkOtpLocationState | null

  const [code, setCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS)
  const [maskedEmail, setMaskedEmail] = useState(state?.maskedEmail ?? '')
  const [lockedOut, setLockedOut] = useState(false)
  const fallbackExpiresAtMsRef = useRef(Date.now() + POLICY_LINK_OTP_TTL_MS)
  const parsedExpiryMs = state?.expiresAtUtc ? Date.parse(state.expiresAtUtc) : Number.NaN
  const expiresAtMs = Number.isFinite(parsedExpiryMs)
    ? parsedExpiryMs
    : fallbackExpiresAtMsRef.current
  const [challengeReusable, setChallengeReusable] = useState(() => expiresAtMs > Date.now())

  useScrollToTopOnErrorSignal(errorMessage)

  const handleLockout = useCallback(
    async (apiBody?: PolicyLinkOtpApiErrorBody) => {
      if (!shouldSignOutForPolicyLinkOtpError(apiBody)) return false
      setLockedOut(true)
      storePolicyLinkOtpLockoutMessage(apiBody)
      try {
        await logout()
      } catch (logoutErr) {
        console.error('[PolicyLinkOtpVerification] logout failed:', logoutErr)
      }
      navigate('/signin', { replace: true })
      return true
    },
    [logout, navigate],
  )

  useEffect(() => {
    if (!state?.challengeId) {
      navigate('/confirm-details', { replace: true })
    }
  }, [state?.challengeId, navigate])

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = window.setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1))
    }, 1000)
    return () => window.clearInterval(timer)
  }, [resendCooldown])

  useEffect(() => {
    if (lockedOut) {
      setChallengeReusable(false)
      return
    }
    const remainingMs = expiresAtMs - Date.now()
    if (remainingMs <= 0) {
      setChallengeReusable(false)
      return
    }
    setChallengeReusable(true)
    const timer = window.setTimeout(() => setChallengeReusable(false), remainingMs)
    return () => window.clearTimeout(timer)
  }, [expiresAtMs, lockedOut])

  const challengeId = state?.challengeId ?? ''

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    const digits = code.replace(/\D/g, '')
    if (digits.length !== 6) {
      setErrorMessage('Enter the 6-digit code from your email.')
      return
    }
    setErrorMessage(null)
    setIsSubmitting(true)
    try {
      const result = await verifyPolicyLinkOtp({ challengeId, code: digits })
      if (result?.linked) {
        sessionStorage.setItem('club4x4_visited_confirm_details', 'true')
        sessionStorage.removeItem('club4x4_policies_cache')
        localStorage.removeItem('club4x4_tutorial_completed')
        if (result.smsMfaEnrolled) {
          // Portal MFA step first (wrong-number / no-SMS escape) — do not jump straight into Auth0.
          navigate('/mfa/verify', { replace: true })
          return
        }
        if (result.smsMfaPhoneMissing) {
          navigate('/mfa/mobile-required', { replace: true })
          return
        }
        navigate('/dashboard', { replace: true })
        return
      }
      setErrorMessage('We could not verify your code. Please try again.')
    } catch (err: unknown) {
      const e = err as Error & { apiBody?: PolicyLinkOtpApiErrorBody }
      if (await handleLockout(e?.apiBody)) return
      setErrorMessage(e?.apiBody?.message ?? e?.message ?? 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || !challengeId || lockedOut) return
    setErrorMessage(null)
    setIsResending(true)
    try {
      const result = await resendPolicyLinkOtp({ challengeId })
      if (result?.maskedEmail) setMaskedEmail(result.maskedEmail)
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (err: unknown) {
      const e = err as Error & {
        apiBody?: PolicyLinkOtpApiErrorBody
      }
      if (await handleLockout(e?.apiBody)) return
      if (e?.apiBody?.code === 'RESEND_COOLDOWN' && typeof e.apiBody.retryAfterSeconds === 'number') {
        setResendCooldown(e.apiBody.retryAfterSeconds)
      }
      setErrorMessage(e?.apiBody?.message ?? e?.message ?? 'Could not resend your code. Please try again.')
    } finally {
      setIsResending(false)
    }
  }, [challengeId, resendCooldown, resendPolicyLinkOtp, lockedOut, handleLockout])

  if (!challengeId) {
    return null
  }

  return (
    <div className="bg-background-brand-primary flex flex-col items-center justify-center min-h-screen w-full">
      <main id="main" className="flex w-full flex-col items-center justify-center">
      <div className="flex flex-col gap-[32px] items-center justify-center px-[16px] py-[80px] relative w-full">
        <Link
          to="/confirm-details"
          className="cursor-pointer flex gap-[4px] items-center p-0 relative w-full max-w-sm"
        >
          <div className="overflow-clip relative shrink-0 size-5 text-foreground-brand-secondary-alt">
            <ChevronLeftIcon />
          </div>
          <span className="font-body font-semibold text-sm leading-sm text-text-brand-secondary-700">
            Change details
          </span>
        </Link>

        <div className="bg-background-primary flex flex-col gap-[24px] items-start p-[24px] relative rounded-2xl shadow-lg w-full max-w-sm">
          <div className="flex flex-col gap-[8px] w-full">
            <h1 className="font-display font-semibold text-display-sm text-text-primary-900 m-0">
              Verify your email
            </h1>
            <p className="font-body text-sm text-text-tertiary-600 m-0">
              We sent a 6-digit code to {maskedEmail || 'the email on your policy'}. It expires in
              10 minutes. Look for an email from Club 4X4 (subject: Club 4X4: your portal verification code).
            </p>
            <p className="font-body text-sm text-text-tertiary-600 m-0">
              If you do not see it within a couple of minutes, check spam, junk, and Gmail
              Promotions.{' '}
              {challengeReusable
                ? 'Stay on this page — going back and submitting the form again will not send a new code.'
                : 'You can go back and submit the form again to send a new code.'}
            </p>
          </div>

          <form onSubmit={handleVerify} className="flex flex-col gap-[16px] w-full">
            {errorMessage ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-900 font-body text-sm" role="alert">
                {errorMessage}
              </div>
            ) : null}

            <div className="flex flex-col gap-[8px]">
              <label htmlFor="policy-link-otp" className="font-body text-sm font-semibold text-text-primary-900">
                Verification code
              </label>
              <input
                id="policy-link-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                value={code}
                disabled={lockedOut}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  setErrorMessage(null)
                }}
                className="w-full rounded-xl border border-solid border-border-primary bg-background-primary px-[14px] py-[12px] font-body text-sm tracking-[0.3em] text-text-primary-900 shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2 disabled:opacity-50"
                aria-invalid={Boolean(errorMessage)}
              />
            </div>

            <PrimaryButton
              type="submit"
              disabled={isSubmitting || lockedOut || code.length !== 6}
              className="w-full"
            >
              {isSubmitting ? 'Verifying…' : 'Verify and link policy'}
            </PrimaryButton>

            <button
              type="button"
              className="font-body text-sm font-semibold text-foreground-brand-solid disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isResending || lockedOut || resendCooldown > 0}
              onClick={() => void handleResend()}
            >
              {resendCooldown > 0
                ? `Check spam first — resend available in ${resendCooldown}s`
                : isResending
                  ? 'Sending…'
                  : 'Resend code'}
            </button>
          </form>
        </div>
      </div>
      </main>
      <Footer />
    </div>
  )
}
